import { useState } from "react";
import { Linkedin, TrendingUp, UserPlus, Users, Archive } from "lucide-react";
import type { Company } from "../api/HiringContactsApi";
import TechTagChip from "shared/TechTagChip";
import { useHiringContacts } from "../modal/HiringContactsContext";
import { WatchToggle, PriorityBadge } from "./ContactCard";
import AssignCompanyModal from "./AssignCompanyModal";
import CompanyContactsModal from "./CompanyContactsModal";

interface Props {
  companies: Company[];
}

export default function CompanyTable({ companies }: Props) {
  const { handleCompanyWatchToggle, handleCompanyPriorityChange, handleArchiveCompany, expandedCompanyId, toggleCompanyExpand } = useHiringContacts();
  const [assignTarget, setAssignTarget] = useState<Company | null>(null);

  if (companies.length === 0) {
    return <p className="text-sm text-muted-foreground font-inter text-center py-10">No unassigned companies found.</p>;
  }

  const modalCompany = companies.find((c) => c.id === expandedCompanyId) ?? null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {companies.map((c) => {
        return (
          <div key={c.id} className="bg-surface/60 border border-border/60 rounded-2xl overflow-hidden">
            <div
              onClick={() => toggleCompanyExpand(c.id)}
              className="flex items-start gap-3 p-4 cursor-pointer hover:bg-surface-muted/40 transition-colors select-none"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-sm font-semibold text-foreground font-inter inline-flex items-center gap-1.5">
                    {c.name}
                    {c.linkedin_url && (
                      <a
                        href={c.linkedin_url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-sky-400"
                      >
                        <Linkedin className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </h3>
                  <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <PriorityBadge priority={c.priority} onChange={(p) => handleCompanyPriorityChange(c.id, p)} />
                    <WatchToggle isWatched={c.is_watched} onToggle={() => handleCompanyWatchToggle(c.id, !c.is_watched)} disabled={!c.linkedin_url} />
                    {c.is_recruiting_agency && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded border bg-amber-500/20 text-amber-300 border-amber-500/40 font-inter font-medium">
                        Agency
                      </span>
                    )}
                  </div>
                </div>

                <p className="text-xs text-muted-foreground font-inter mt-0.5">{c.domain}</p>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-muted-foreground font-inter">
                  {c.industry && <span>{c.industry}</span>}
                  {c.employee_count_range && <span>· {c.employee_count_range} employees</span>}
                  {typeof c.num_jobs_last_30_days === "number" && c.num_jobs_last_30_days > 0 && (
                    <span className="flex items-center gap-0.5 text-emerald-400">
                      <TrendingUp className="w-3 h-3" />
                      {c.num_jobs_last_30_days} jobs/30d
                    </span>
                  )}
                </div>

                {c.tech_tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {c.tech_tags.slice(0, 6).map((tag) => (
                      <TechTagChip key={tag} label={tag} />
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => toggleCompanyExpand(c.id)}
                    className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground font-inter transition-colors"
                  >
                    <Users className="w-3 h-3" />
                    View contacts
                  </button>
                  <button
                    onClick={() => handleArchiveCompany(c.id)}
                    title="Archiving also archives every contact under this company"
                    className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground hover:text-destructive bg-surface-muted hover:bg-destructive/10 border border-border hover:border-destructive/30 rounded-lg px-2 py-1 transition-colors font-inter"
                  >
                    <Archive className="w-3 h-3" />
                    Archive
                  </button>
                  <button
                    onClick={() => setAssignTarget(c)}
                    className="flex items-center gap-1 text-[11px] text-primary hover:text-primary-foreground bg-primary/20 hover:bg-primary border border-primary/30 hover:border-primary rounded-lg px-2 py-1 transition-colors font-inter"
                  >
                    <UserPlus className="w-3 h-3" />
                    Assign
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {assignTarget && (
        <AssignCompanyModal company={assignTarget} onClose={() => setAssignTarget(null)} />
      )}
      {modalCompany && (
        <CompanyContactsModal company={modalCompany} onClose={() => toggleCompanyExpand(modalCompany.id)} />
      )}
    </div>
  );
}
